import createClient, { type Middleware } from 'openapi-fetch';
import Cookies from 'universal-cookie';

import type { paths } from 'src/api/openapi-client';
import { assertDefined } from 'src/utils';

import type {
  DeliverabilityBlockKeyEnum,
  MaverickProductEnum,
  RoleEnum,
  SelectedProductEnum,
  SetupSkippedReasonEnum,
  SetupStatusEnum,
  ShardTypeEnum,
  SPOptimizerSetupDecisionStatusEnum,
  TargetMetricEnum,
  TestingSplitEnum,
  UserAccountEnum,
} from './models';

export const mapMaverickDemoStoreKey: Record<string, string> = {
  maverickdemo: 'magicmind',
  maverickdemo3: 'cakesbody',
  maverickdemovideo: 'sassysaintsbringsthesalonhome',
  maverickdemooptimizer: 'magicmind',
};
export const mapMaverickDemoStoreId: Record<string, number> = {
  503: 30, //maverickdemo -> magicmind
  1227: 711, //maverickdemo3 -> cakesbody
  1233: 463, //maverickdemovideo -> sassysaintsbringsthesalonhome
  1234: 30, // maverickdemooptimizer -> magicmind
};
export const mapMaverickDemoOptimizerStoreId: Record<number, number> = {
  226: 160, //maverickdemo -> magicmind
  224: 104, //maverickdemo3 -> cakesbody
  231: 22, //maverickdemovideo -> sassysaintsbringsthesalonhome
  232: 160, //maverickdemooptimizer -> magicmind
};
export const mapMaverickDemoOrganization: Record<string, string> = {
  maverickdemo: 'magicmind', //maverickdemo -> magicmind
  maverickdemo3: 'cakesbody', //maverickdemo3 -> cakesbody
  maverickdemovideo: 'sassysaintsbringsthesalonhome', //maverickdemovideo -> sassysaintsbringsthesalonhome
  maverickdemooptimizer: 'magicmind', //maverickdemooptimizer -> magicmind
};

function getCsrfValue(): string {
  const cookies = new Cookies(null, { path: '/' });
  return cookies.get<string>('csrftoken');
}

const addCsrfToken: Middleware = {
  onRequest({ request }) {
    request.headers.set('X-CSRFTOKEN', getCsrfValue());
    return request;
  },
};

const throwOnError: Middleware = {
  async onResponse({ response }) {
    if (response.status >= 400) {
      const body: unknown = response.headers.get('content-type')?.includes('json')
        ? await response.clone().json()
        : await response.clone().text();
      throw new Error(`API request error: status=${response.status}`, { cause: body });
    }
    return undefined;
  },
};

export const client = createClient<paths>({ baseUrl: '/' });
client.use(addCsrfToken);
client.use(throwOnError);

export async function postFeedback(
  organization: string,
  videoUrl: string,
  message: string,
  email: string | null,
  phone: string | null,
  deliveryMethod: 'SMS' | 'EMAIL' | null,
) {
  const response = await client.POST('/mapi/feedbacks/', {
    params: {
      query: {
        organization: organization,
      },
    },
    body: {
      video: videoUrl,
      text: message,
      email: email,
      phoneNumber: phone,
      deliveryMethod: deliveryMethod,
    },
  });
  assertDefined(response.data);
  return response.data;
}

export async function postCreateUsecasesFromOnboarding(organization: string, storeKey: string, allUseCases: string) {
  const response = await client.POST('/mapi/stores/{keyName}/create_use_cases_from_onboarding/', {
    params: {
      path: { keyName: storeKey },
      query: { organization: organization, allUseCases: allUseCases },
    },
  });
  return response.response.ok;
}

export async function getStores() {
  const response = await client.GET('/mapi/stores/', {});
  assertDefined(response.data);
  return response.data;
}

export async function getGrowthReport(product: 'all' | 'video' | 'optimizer') {
  const response = await client.GET('/mapi/growth-report/', { params: { query: { product } } });
  assertDefined(response.data);
  return response.data;
}

export async function getEnabledStores() {
  const response = await client.GET('/mapi/enabled-stores/');
  assertDefined(response.data);
  return response.data;
}

export async function getChurnedStores() {
  const response = await client.GET('/mapi/churned-stores/');
  assertDefined(response.data);
  return response.data;
}

export async function getEnabledStoresFinancials() {
  const response = await client.GET('/mapi/enabled-stores/financials/');
  assertDefined(response.data);
  return response.data;
}

export async function postOffboardStore(storeId: number, product: 'video' | 'optimizer' | 'campaign' | 'flow') {
  const response = await client.POST('/mapi/enabled-stores/offboard/', {
    body: { storeId, product },
  });
  assertDefined(response.data);
  return response.data;
}

export async function postSendMonthlyDigest(storeId: number, test: boolean, startDate: string, endDate: string) {
  const response = await client.POST('/mapi/enabled-stores/send-monthly-digest/', {
    body: { storeId, test, startDate, endDate },
  });
  assertDefined(response.data);
  return response.data;
}

export async function getBrowserAutomationSessions(page = 1, pageSize = 50, search?: string) {
  const response = await client.GET('/optimizer/browser_automation_sessions/', {
    params: {
      query: {
        limit: pageSize,
        offset: (page - 1) * pageSize,
        ...(search?.trim() ? { search: search.trim() } : {}),
      },
    },
  });
  assertDefined(response.data);
  return response.data;
}

export async function getBrowserAutomationSession(id: number) {
  const response = await client.GET('/optimizer/browser_automation_sessions/{id}/', {
    params: { path: { id } },
  });
  assertDefined(response.data);
  return response.data;
}

export async function getBrowserAutomationHealth() {
  const response = await client.GET('/optimizer/browser_automation_sessions/health/');
  assertDefined(response.data);
  return response.data;
}

export type CSIdentity = 'team' | 'support';

export async function getCSBrowserSessions(identity: CSIdentity, pageSize = 20) {
  const response = await client.GET('/optimizer/cs_browser_sessions/', {
    params: { query: { identity, limit: pageSize } },
  });
  assertDefined(response.data);
  return response.data;
}

export async function postCSBrowserSessionStart(identity: CSIdentity) {
  const response = await client.POST('/optimizer/cs_browser_sessions/start/', { body: { identity } });
  assertDefined(response.data);
  return response.data;
}

export async function postCSBrowserSessionEnd(id: number) {
  const response = await client.POST('/optimizer/cs_browser_sessions/{id}/end/', {
    params: { path: { id } },
  });
  assertDefined(response.data);
  return response.data;
}

export async function postCSBrowserSessionCancel(id: number) {
  const response = await client.POST('/optimizer/cs_browser_sessions/{id}/cancel/', {
    params: { path: { id } },
  });
  assertDefined(response.data);
  return response.data;
}

export async function postCSBrowserSessionRenew(id: number) {
  const response = await client.POST('/optimizer/cs_browser_sessions/{id}/renew/', {
    params: { path: { id } },
  });
  assertDefined(response.data);
  return response.data;
}

export interface CSPresenceInput {
  identity: CSIdentity;
  cursorX?: number | null;
  cursorY?: number | null;
  overView?: boolean;
  wantControl?: 'take' | 'release';
}

export async function postCSBrowserSessionPresence(input: CSPresenceInput) {
  const response = await client.POST('/optimizer/cs_browser_sessions/presence/', { body: input });
  assertDefined(response.data);
  return response.data;
}

export async function getTrialExpirations() {
  const response = await client.GET('/mapi/trial-expirations/');
  assertDefined(response.data);
  return response.data;
}

export async function getTrialExpirationsIncrementalRevenue() {
  const response = await client.GET('/mapi/trial-expirations/incremental-revenue/');
  assertDefined(response.data);
  return response.data;
}

export async function getOrganizationMembers(slug: string) {
  const response = await client.GET('/core/organization/{slug}/members/', {
    params: { path: { slug } },
  });
  assertDefined(response.data);
  return response.data;
}

export async function postAddOrganizationMember(slug: string, email: string, role: RoleEnum) {
  const response = await client.POST('/core/organization/{slug}/add_member/', {
    params: { path: { slug } },
    body: { email: email, role: role },
  });
  assertDefined(response.data);
  return response.data;
}

export async function postRemoveOrganizationMember(slug: string, userId: number) {
  const response = await client.POST('/core/organization/{slug}/remove_member/', {
    params: { path: { slug } },
    body: { userId: userId },
  });
  return response.response.ok;
}

export async function postUpdateOrganizationMemberRole(slug: string, userId: number, role: RoleEnum) {
  const response = await client.POST('/core/organization/{slug}/update_member_role/', {
    params: { path: { slug } },
    body: { userId: userId, role: role },
  });
  assertDefined(response.data);
  return response.data;
}

// todo: do we have safe-guards in place to prevent users from creating multiple stores?
// TODO: now we create store with selected product set to null then set it to the selection in next step. ok or change?
export async function postStoreInfo(
  keyName: string,
  storeName: string,
  contactAddresses: string[],
  updateLogoValue: boolean,
) {
  const response = await client.POST('/mapi/stores/', {
    body: {
      keyName: keyName,
      companyName: storeName,
      contactAddresses: contactAddresses,
      updateLogoValue: updateLogoValue,
    },
  });
  assertDefined(response.data);
  return response.data;
}

export async function deleteSPOptimizerStoreExcludePhrase(organization: string, id: number) {
  const response = await client.DELETE('/optimizer/store_exclude_phrase/{id}/', {
    params: {
      path: { id: id },
      query: { organization: organization },
    },
  });
  assertDefined(response.data);
  return response.data;
}

export async function deleteSPOptimizerStoreIncludePhrase(organization: string, id: number) {
  const response = await client.DELETE('/optimizer/store_include_phrase/{id}/', {
    params: {
      path: { id: id },
      query: { organization: organization },
    },
  });
  assertDefined(response.data);
  return response.data;
}

export async function getSPOptimizerStoreExcludePhrases(organization: string, spOptimizerStoreID: number) {
  const response = await client.GET('/optimizer/store_exclude_phrase/', {
    params: {
      query: {
        organization: organization,
        spOptimizerStore: spOptimizerStoreID,
      },
    },
  });
  assertDefined(response.data);
  return response.data;
}

export async function getSPOptimizerStoreIncludePhrases(organization: string, spOptimizerStoreID: number) {
  const response = await client.GET('/optimizer/store_include_phrase/', {
    params: {
      query: {
        organization: organization,
        spOptimizerStore: spOptimizerStoreID,
      },
    },
  });
  assertDefined(response.data);
  return response.data;
}

export async function postSPOptimizerStoreExcludePhrase(
  organization: string,
  spOptimizerStore: number,
  phrase: string,
) {
  const response = await client.POST('/optimizer/store_exclude_phrase/', {
    params: { query: { organization: organization } },
    body: {
      spOptimizerStore: spOptimizerStore,
      phrase: phrase,
    },
  });
  assertDefined(response.data);
  return response.data;
}

export async function postSPOptimizerStoreIncludePhrase(
  organization: string,
  spOptimizerStore: number,
  phrase: string,
) {
  const response = await client.POST('/optimizer/store_include_phrase/', {
    params: { query: { organization: organization } },
    body: {
      spOptimizerStore: spOptimizerStore,
      phrase: phrase,
    },
  });
  assertDefined(response.data);
  return response.data;
}

export async function patchSPOptimizerFlowMessageExperiment(
  organization: string,
  id: number,
  targetMetric: TargetMetricEnum,
) {
  const response = await client.PATCH('/optimizer/flow_message_experiment/{id}/', {
    params: {
      path: { id: id },
      query: { organization: organization },
    },
    body: {
      newTargetMetric: targetMetric,
    },
  });
  assertDefined(response.data);
  return response.data;
}

export async function patchSPOptimizerStore(
  organization: string,
  id: number,
  brandVoicePrompt?: string,
  storeDescription?: string,
  firstNameFallback?: string,
  onboardedAt?: string,
) {
  const response = await client.PATCH('/optimizer/store/{id}/', {
    params: {
      path: { id: id },
      query: { organization: organization },
    },
    body: {
      ...(brandVoicePrompt !== undefined && { brandVoicePrompt }),
      ...(storeDescription !== undefined && { storeDescription }),
      ...(firstNameFallback !== undefined && { firstNameFallback }),
      ...(onboardedAt !== undefined && { onboardedAt }),
    },
  });
  assertDefined(response.data);
  return response.data;
}

export async function patchStoreInfo(
  organization: string,
  keyName: string,
  companyName: string,
  contactAddresses: string[],
  updateLogoValue: boolean,
) {
  const response = await client.PATCH('/mapi/stores/{keyName}/', {
    params: {
      path: { keyName: keyName },
      query: { organization: organization },
    },
    body: {
      keyName: keyName,
      companyName: companyName,
      contactAddresses: contactAddresses,
      updateLogoValue: updateLogoValue,
    },
  });
  assertDefined(response.data);
  return response.data;
}

export async function getUsecase(organization: string, usecaseId: number) {
  const response = await client.GET('/mapi/usecases/{id}/', {
    params: {
      path: { id: usecaseId },
      // Demo accounts (`maverickdemovideo`, etc.) list use cases that live under the underlying
      // organization (e.g. `sassysaintsbringsthesalonhome`) — those ids 404 against the raw demo
      // organization. Apply the same mapping the list endpoint already uses so detail pages work.
      query: { organization: mapMaverickDemoOrganization[organization] ?? organization },
    },
  });
  assertDefined(response.data);
  return response.data;
}

export async function getFlowMessage(flowMessageID: number) {
  const response = await client.GET('/klaviyo/flow_messages/{id}/', {
    params: { path: { id: flowMessageID } },
  });
  assertDefined(response.data);
  return response.data;
}

export async function getStore(organization: string, keyName: string) {
  const response = await client.GET('/mapi/stores/{keyName}/', {
    params: {
      path: { keyName: keyName },
      query: { organization: organization },
    },
  });
  assertDefined(response.data);
  return response.data;
}

export type UsecasesForStoreData = Awaited<ReturnType<typeof getUsecasesForStore>>;
export async function getUsecasesForStore(organization: string | undefined, storeKey: string | undefined) {
  if (!organization || !storeKey) {
    return { count: 0, results: [] };
  }
  const response = await client.GET('/mapi/usecases/', {
    params: {
      query: {
        store_KeyName: mapMaverickDemoStoreKey[storeKey] ?? storeKey,
        organization: mapMaverickDemoOrganization[organization] ?? organization,
      },
    },
  });
  assertDefined(response.data);
  return response.data;
}

export async function getSPOptimizerStores(organization: string) {
  const response = await client.GET('/optimizer/store/', {
    params: { query: { organization: mapMaverickDemoOrganization[organization] ?? organization } },
  });
  assertDefined(response.data);
  return response.data;
}

export async function getSPOptimizerStore(organization: string, spOptimizerStoreID: number) {
  const response = await client.GET('/optimizer/store/{id}/', {
    params: {
      path: { id: mapMaverickDemoOptimizerStoreId[spOptimizerStoreID] ?? spOptimizerStoreID },
      query: { organization: mapMaverickDemoOrganization[organization] ?? organization },
    },
  });
  assertDefined(response.data);
  return response.data;
}

export async function getSPOptimizerFlowMessageExperimentMetricsForSPOptimizerStore(
  organization: string,
  spOptimizerStoreID: number,
) {
  const response = await client.GET('/optimizer/store/{id}/all_flow_experiment_last_30d_metrics/', {
    params: {
      path: { id: mapMaverickDemoOptimizerStoreId[spOptimizerStoreID] ?? spOptimizerStoreID },
      query: { organization: mapMaverickDemoOrganization[organization] ?? organization },
    },
  });
  assertDefined(response.data);
  return response.data;
}

export type SPOptimizerFlowMessageExperimentsForStoreData = Awaited<
  ReturnType<typeof getSPOptimizerFlowMessageExperimentsForStore>
>;
export async function getSPOptimizerFlowMessageExperimentsForStore(
  organization: string | undefined,
  storeKey: string | undefined,
) {
  if (!organization || !storeKey) {
    return { count: 0, results: [] };
  }

  const response = await client.GET('/optimizer/flow_message_experiment/', {
    params: {
      query: {
        spOptimizerStore_Store_KeyName: mapMaverickDemoStoreKey[storeKey] ?? storeKey,
        organization: mapMaverickDemoOrganization[organization] ?? organization,
      },
    },
  });
  assertDefined(response.data);
  return response.data;
}

export async function getSPOptimizerFlowMessageExperimentsForFlowMessage(
  organization: string | undefined,
  flowMessageID: number | undefined,
) {
  if (!organization || !flowMessageID) {
    return { count: 0, results: [] };
  }

  const response = await client.GET('/optimizer/flow_message_experiment/', {
    params: {
      query: {
        flowMessage_Id: flowMessageID,
        organization: organization,
      },
    },
  });
  assertDefined(response.data);
  return response.data;
}

export async function getSPOptimizerFlowMessageExperimentsForSPOptimizerStore(
  organization: string,
  spOptimizerStoreId: number | undefined,
) {
  if (!spOptimizerStoreId) {
    return { count: 0, results: [] };
  }
  const response = await client.GET('/optimizer/flow_message_experiment/', {
    params: {
      query: {
        spOptimizerStore_Id: mapMaverickDemoOptimizerStoreId[spOptimizerStoreId] ?? spOptimizerStoreId,
        enabled: true,
        organization: mapMaverickDemoOrganization[organization] ?? organization,
      },
    },
  });
  assertDefined(response.data);
  return response.data;
}

export async function getCustomerFeedbacksForUsecase(organization: string, usecaseId: number, page = 1, pageSize = 10) {
  const response = await client.GET('/mapi/feedbacks/', {
    params: {
      query: {
        video__use_case: usecaseId,
        page: page,
        page_size: pageSize,
        organization: organization,
      },
    },
  });
  assertDefined(response.data);
  return response.data;
}

export async function getCustomerFeedbacksForStore(
  organization: string,
  storeId: number | undefined,
  page = 1,
  pageSize = 25,
) {
  if (!storeId) {
    return { count: 0, results: [] };
  }
  const response = await client.GET('/mapi/feedbacks/', {
    params: {
      query: {
        video__use_case__store: mapMaverickDemoStoreId[storeId] ?? storeId,
        page: page,
        page_size: pageSize,
        organization: mapMaverickDemoOrganization[organization] ?? organization,
      },
    },
  });
  assertDefined(response.data);
  return response.data;
}

export async function getSignedMediaUploadUrlForUseCase(organization: string, useCaseId: number, filename: string) {
  const response = await client.GET('/mapi/usecases/{id}/signed_media_upload_url/', {
    params: {
      path: { id: useCaseId },
      query: { filename: filename, organization: organization },
    },
  });
  assertDefined(response.data);
  return response.data;
}

export async function getSignedLogoUploadUrlForUser(userID: number, filename: string) {
  const response = await client.GET('/mapi/users/{id}/signed_logo_upload_url/', {
    params: {
      path: { id: userID },
      query: { filename: filename },
    },
  });
  assertDefined(response.data);
  return response.data;
}

export async function postEnableSPOptimizerFlowMessageExperiment(
  organization: string,
  spOptimizerFlowMessageExperimentId: number,
) {
  const response = await client.POST('/optimizer/flow_message_experiment/{id}/enable_experiment/', {
    params: {
      path: { id: spOptimizerFlowMessageExperimentId },
      query: { organization: organization },
    },
  });
  return response.response.ok;
}

export async function postDisableSPOptimizerFlowMessageExperiment(
  organization: string,
  spOptimizerFlowMessageExperimentId: number,
) {
  const response = await client.POST('/optimizer/flow_message_experiment/{id}/disable_experiment/', {
    params: {
      path: { id: spOptimizerFlowMessageExperimentId },
      query: { organization: organization },
    },
  });
  return response.response.ok;
}

export async function postDidUploadLogoForUser(userID: number, filename: string, uploadFilename: string) {
  const response = await client.POST('/mapi/users/{id}/did_upload_logo/', {
    params: {
      path: { id: userID },
      query: { filename: filename },
    },
    body: { uploadFilename: uploadFilename },
  });
  return response.response.ok;
}

export async function postDidUploadUsecaseMedia(
  organization: string,
  useCaseId: number,
  filename: string,
  uploadFilename: string,
) {
  const response = await client.POST('/mapi/usecases/{id}/did_upload_usecase_media/', {
    params: {
      path: { id: useCaseId },
      query: { filename: filename, organization: organization },
    },
    body: { uploadFilename: uploadFilename },
  });
  assertDefined(response.data);
  return response.data;
}

export async function patchUsecaseOnboardingState(organization: string, useCaseId: number, onboardingState: string) {
  const response = await client.PATCH('/mapi/usecases/{id}/update_onboarding_state/', {
    params: {
      path: { id: useCaseId },
      query: { organization: organization },
    },
    body: {
      onboardingState: onboardingState,
    },
  });
  // The endpoint returns 204 No Content on success, so there is no body to
  // assert on. `throwOnError` middleware already raises on any HTTP >= 400
  // (e.g. the NOT_STARTED/template-video guards), so reaching here means the
  // transition succeeded — report ok rather than asserting a (null) body.
  return response.response.ok;
}

export async function getKlaviyoAccount(organization: string, klaviyoAccountId: number) {
  if (klaviyoAccountId == 0) {
    return {
      display_api_key: '',
      organization_name: '',
      owner: '',
    };
  }
  const response = await client.GET('/klaviyo/accounts/{id}/', {
    params: {
      path: { id: klaviyoAccountId },
      query: { organization: organization },
    },
  });
  assertDefined(response.data);
  return response.data;
}

/** Superuser-only (admin mode): deletes the Klaviyo account's OAuth token and deactivates
 *  the account so an admin can force a fresh reconnect, instead of deleting the
 *  SocialToken row in Django admin. Reconnecting reactivates the account. */
export async function postDisconnectKlaviyoAccount(klaviyoAccountId: number) {
  const response = await client.POST('/klaviyo/accounts/{id}/disconnect/', {
    params: { path: { id: klaviyoAccountId } },
  });
  assertDefined(response.data);
  return response.data;
}

export async function getShopifyShop(organization: string, shopifyShopId: number) {
  if (shopifyShopId == 0) {
    // Shape must match the camelCased API response below (the REST layer runs
    // djangorestframework-camel-case), so callers see one consistent type.
    return {
      accessScopeIsCurrent: true,
      missingAccessScopes: [],
      id: 0,
      latestProcessedCheckoutTs: null,
      myshopifyDomain: '',
      owner: '',
      shopifyShopId: '',
    };
  }
  const response = await client.GET('/shopify_app/shops/{id}/', {
    params: {
      path: { id: shopifyShopId },
      query: { organization: organization },
    },
  });
  assertDefined(response.data);
  return response.data;
}

export async function getStoreAggregatedMetrics(
  organization: string,
  storeKey: string,
  days: number,
  useCaseId?: number,
) {
  const response = await client.GET('/mapi/stores/{keyName}/aggregated_metrics/', {
    params: {
      path: { keyName: mapMaverickDemoStoreKey[storeKey] ?? storeKey },
      query: {
        days: days,
        useCaseId: useCaseId ?? 0,
        organization: mapMaverickDemoOrganization[organization] ?? organization,
      },
    },
  });
  assertDefined(response.data);
  return response.data;
}

export async function getStoreAggregatedMetricsPerUseCase(organization: string, storeKey: string, days: number) {
  const response = await client.GET('/mapi/stores/{keyName}/aggregated_metrics_per_use_case/', {
    params: {
      path: { keyName: mapMaverickDemoStoreKey[storeKey] ?? storeKey },
      query: {
        days: days,
        organization: mapMaverickDemoOrganization[organization] ?? organization,
      },
    },
  });
  assertDefined(response.data);
  return response.data;
}

export async function getSPOptimizerStoreAggregatedSummaryFlowMetrics(
  organization: string,
  spOptimizerStoreId: number,
  days: number,
  flowId?: number,
  flowMessageId?: number,
  flowMessageVariantId?: number,
  startDate?: string,
  endDate?: string,
) {
  const response = await client.GET('/optimizer/store/{id}/aggregated_summary_flow_metrics/', {
    params: {
      path: { id: mapMaverickDemoOptimizerStoreId[spOptimizerStoreId] ?? spOptimizerStoreId },
      query: {
        organization: mapMaverickDemoOrganization[organization] ?? organization,
        days: days,
        flowId: flowId ?? 0,
        flowMessageId: flowMessageId ?? 0,
        flowMessageVariantId: flowMessageVariantId ?? 0,
        startDate: startDate ?? '',
        endDate: endDate ?? '',
      },
    },
  });
  assertDefined(response.data);
  return response.data;
}

interface FlowMetrics {
  incremental_cents_driven?: number | null;
  incremental_conversion_rate?: number | null;
  incremental_orders_driven?: number | null;
  incremental_click_rate?: number | null;
  incremental_clicks_driven?: number | null;
  incremental_open_rate?: number | null;
  incremental_opens_driven?: number | null;
}

interface BatchFlowMetricsResponse {
  flow_metrics: Record<string, FlowMetrics>;
}

export async function getSPOptimizerStoreBatchAggregatedSummaryFlowMetrics(
  organization: string,
  spOptimizerStoreId: number,
  days: number,
  flowId?: number,
  startDate?: string,
  endDate?: string,
): Promise<BatchFlowMetricsResponse> {
  const params = new URLSearchParams({
    organization: mapMaverickDemoOrganization[organization] ?? organization,
    days: String(days),
  });
  if (flowId) params.append('flow_id', String(flowId));
  if (startDate) params.append('start_date', startDate);
  if (endDate) params.append('end_date', endDate);

  const mappedStoreId = mapMaverickDemoOptimizerStoreId[spOptimizerStoreId] ?? spOptimizerStoreId;
  const url = `/optimizer/store/${mappedStoreId}/batch_aggregated_summary_flow_metrics/?${params.toString()}`;
  const response = await fetch(url);
  const data = (await response.json()) as BatchFlowMetricsResponse;
  return data;
}

export async function getSPOptimizerFlowMessageExperimentVariantAggregatedMetrics(
  organization: string,
  spOptimizerFlowMessageExperimentVariantId: number,
  days: number,
  startDate?: string,
  endDate?: string,
) {
  const response = await client.GET('/optimizer/flow_message_experiment_variant/{id}/aggregated_metrics/', {
    params: {
      path: { id: spOptimizerFlowMessageExperimentVariantId },
      query: {
        organization: organization,
        days: days,
        startDate: startDate ?? '',
        endDate: endDate ?? '',
      },
    },
  });
  assertDefined(response.data);
  return response.data;
}

export async function getStoreDailyMetrics(organization: string, storeKey: string, days: number, useCaseId?: number) {
  const response = await client.GET('/mapi/stores/{keyName}/daily_metrics/', {
    params: {
      path: { keyName: mapMaverickDemoStoreKey[storeKey] ?? storeKey },
      query: {
        days: days,
        useCaseId: useCaseId ?? 0,
        organization: mapMaverickDemoOrganization[organization] ?? organization,
      },
    },
  });
  assertDefined(response.data);
  return response.data;
}

export async function getStoreConsentOptinSummary(organization: string, storeKey: string, days: number) {
  const response = await client.GET('/mapi/stores/{keyName}/consent_optin_summary/', {
    params: {
      path: { keyName: mapMaverickDemoStoreKey[storeKey] ?? storeKey },
      query: {
        days: days,
        organization: mapMaverickDemoOrganization[organization] ?? organization,
      },
    },
  });
  assertDefined(response.data);
  return response.data;
}

export async function getFlowMonthlyOptimizerMetrics(
  organization: string | undefined,
  spOptimizerStoreId: number | undefined,
) {
  if (!organization || !spOptimizerStoreId) return;

  const response = await client.GET('/optimizer/store/{id}/flow_monthly_metrics/', {
    params: {
      path: { id: mapMaverickDemoOptimizerStoreId[spOptimizerStoreId] ?? spOptimizerStoreId },
      query: { organization: mapMaverickDemoOrganization[organization] ?? organization },
    },
  });
  assertDefined(response.data);
  return response.data;
}

export async function getCampaignMonthlyDeliverabilityMetrics(
  organization: string,
  spOptimizerStoreId: number | undefined,
) {
  if (!spOptimizerStoreId) return;
  const response = await client.GET('/optimizer/store/{id}/campaign_monthly_deliverability_metrics/', {
    params: {
      path: { id: mapMaverickDemoOptimizerStoreId[spOptimizerStoreId] ?? spOptimizerStoreId },
      query: { organization: mapMaverickDemoOrganization[organization] ?? organization },
    },
  });
  assertDefined(response.data);
  return response.data;
}

export async function getCombinedMonthlyOptimizerMetrics(
  organization: string,
  spOptimizerStoreId: number | undefined,
  includeCampaigns?: boolean,
  includeFlows?: boolean,
) {
  if (!spOptimizerStoreId) return;
  const response = await client.GET('/optimizer/store/{id}/combined_monthly_metrics/', {
    params: {
      path: { id: mapMaverickDemoOptimizerStoreId[spOptimizerStoreId] ?? spOptimizerStoreId },
      query: {
        organization: mapMaverickDemoOrganization[organization] ?? organization,
        includeCampaigns: includeCampaigns ?? true,
        includeFlows: includeFlows ?? true,
      },
    },
  });
  assertDefined(response.data);
  return response.data;
}

export async function postShopifyCartProductImages(
  organization: string,
  storeKey: string,
  shopifyCheckoutId: string | undefined | null,
  shopifyCheckoutToken: string | undefined | null,
  shopifyProductIds: string[],
) {
  const response = await client.POST('/mapi/stores/{keyName}/shopify_cart_product_images/', {
    params: {
      path: { keyName: storeKey },
      query: { organization: organization },
    },
    body: {
      ...(shopifyCheckoutId ? { shopifyCheckoutId: shopifyCheckoutId } : {}),
      ...(shopifyCheckoutToken ? { shopifyCheckoutToken: shopifyCheckoutToken } : {}),
      shopifyProductIds: shopifyProductIds,
    },
  });
  assertDefined(response.data);
  return response.data;
}

export async function getSavedVideoScripts(organization: string, useCaseId: number) {
  const response = await client.GET('/mapi/usecases/{id}/saved_video_scripts/', {
    params: {
      path: { id: useCaseId },
      query: { organization: organization },
    },
  });
  assertDefined(response.data);
  return response.data;
}

export async function postSaveVideoScripts(organization: string, useCaseId: number, script: string) {
  const response = await client.POST('/mapi/usecases/{id}/save_video_script/', {
    params: {
      path: { id: useCaseId },
      query: { organization: organization },
    },
    body: { script: script },
  });
  assertDefined(response.data);
  return response.data;
}

export async function postSaveSelectedProduct(organization: string, storeKey: string, product: SelectedProductEnum) {
  const response = await client.POST('/mapi/stores/{keyName}/save_selected_product/', {
    params: {
      path: { keyName: storeKey },
      query: { organization: organization },
    },
    body: { selectedProduct: product },
  });
  assertDefined(response.data);
  return response.data;
}

export async function postCreateSPOptimizerStoreFromFlowMessage(
  organization: string,
  flowMessageId: number,
  storeAboutPageUrl?: string,
) {
  const response = await client.POST('/optimizer/store/create_from_flow_message/', {
    params: { query: { organization: organization } },
    body: {
      flowMessageId: flowMessageId,
      ...(storeAboutPageUrl ? { storeAboutPageUrl } : {}),
    },
  });
  assertDefined(response.data);
  return response.data;
}

export async function replaceSPOptimizerFlowMessageExperimentBrandOriginalVariant(
  organization: string,
  spOptimizerFlowMessageExperimentVariantId: number,
  subject: string,
  preview: string,
) {
  const response = await client.POST('/optimizer/flow_message_experiment_variant/{id}/replace_brand_original/', {
    body: {
      subject: subject,
      preview: preview,
    },
    params: {
      path: {
        id: spOptimizerFlowMessageExperimentVariantId,
      },
      query: { organization: organization },
    },
  });
  assertDefined(response.data);
  return response.data;
}

export async function postDeactivateSPOptimizerFlowMessageExperimentVariant(
  organization: string,
  spOptimizerFlowMessageExperimentVariantId: number,
) {
  const response = await client.POST('/optimizer/flow_message_experiment_variant/{id}/deactivate/', {
    params: {
      path: {
        id: spOptimizerFlowMessageExperimentVariantId,
      },
      query: { organization: organization },
    },
  });
  assertDefined(response.data);
  return response.data;
}

export async function postCreateSPOptimizerStoreFromStore(
  organization: string,
  storeId: number,
  uiEnabled: boolean,
  storeAboutPageUrl?: string,
) {
  const response = await client.POST('/optimizer/store/create_from_store/', {
    params: { query: { organization: organization } },
    body: {
      storeId: storeId,
      uiEnabled: uiEnabled,
      ...(storeAboutPageUrl ? { storeAboutPageUrl } : {}),
    },
  });
  assertDefined(response.data);
  return response.data;
}

export async function postChangeUiEnabledForSPOptimizerStore(
  organization: string,
  storeId: number,
  uiEnabled: boolean,
) {
  const response = await client.POST('/optimizer/store/update_ui_enabled_for_store/', {
    params: { query: { organization: organization } },
    body: {
      uiEnabled: uiEnabled,
      storeId: storeId,
    },
  });
  assertDefined(response.data);
  return response.data;
}

export async function regenerateDescriptionFromAboutPageUrl(
  organization: string,
  spOptimizerStoreId: number,
  storeAboutPageUrl: string,
) {
  const response = await client.POST('/optimizer/store/{id}/regenerate_description_from_about_page_url/', {
    params: {
      path: { id: spOptimizerStoreId },
      query: { organization: organization },
    },
    body: { storeAboutPageUrl: storeAboutPageUrl },
  });
  assertDefined(response.data);
  return response.data;
}

export async function postCreateSPOptimizerFlowMessageExperimentFromFlowMessage(
  organization: string,
  flowMessageId: number,
  spOptimizerStoreId: number,
) {
  const response = await client.POST('/optimizer/flow_message_experiment/create_from_flow_message/', {
    params: { query: { organization: organization } },
    body: {
      flowMessageId: flowMessageId,
      spOptimizerStoreId: spOptimizerStoreId,
      messageType: 'OTHER',
    },
  });
  assertDefined(response.data);
  return response.data;
}

export async function postSPOptimizerFlowMessageExperimentSetWinnerShard(
  organization: string,
  spOptimizerFlowMessageExperimentId: number,
  winnerShard: ShardTypeEnum,
) {
  const response = await client.POST('/optimizer/flow_message_experiment/{id}/select_winner/', {
    params: {
      path: {
        id: spOptimizerFlowMessageExperimentId,
      },
      query: { organization: organization },
    },
    body: {
      winnerShard: winnerShard,
    },
  });
  assertDefined(response.data);
  return response.data;
}

export async function postSPOptimizerStoreBulkCreateNewFlowPhase(organization: string, spOptimizerStoreId: number) {
  const response = await client.POST('/optimizer/store/bulk_create_new_flow_phase/', {
    params: { query: { organization: organization } },
    body: {
      spOptimizerStoreId: spOptimizerStoreId,
    },
  });
  assertDefined(response.data);
  return response.data;
}

export async function getFlowsForStoreKey(storeKey: string) {
  const response = await client.GET('/klaviyo/flows/', {
    params: {
      query: {
        account_Store_KeyName: storeKey,
        limit: 1500,
      },
    },
  });
  assertDefined(response.data);
  return response.data;
}

export async function getFlowActionsForStoreKey(storeKey: string) {
  const response = await client.GET('/klaviyo/flow_actions/', {
    params: { query: { flow_Account_Store_KeyName: storeKey, limit: 5000 } },
  });
  assertDefined(response.data);
  return response.data;
}

export async function getFlowMessagesForStoreKey(storeKey: string) {
  const response = await client.GET('/klaviyo/flow_messages/', {
    params: {
      query: {
        flowAction_Flow_Account_Store_KeyName: storeKey,
        limit: 5000,
      },
    },
  });
  assertDefined(response.data);
  return response.data;
}

export async function getCampaignCoverageAlerts(weekStart?: string) {
  const response = await client.GET(
    '/optimizer/get_campaign_coverage_alerts/' as any, // oxlint-disable-line typescript/no-explicit-any
    { params: { query: weekStart ? { week_start: weekStart } : {} } },
  );
  assertDefined(response.data);
  return response.data as {
    week_of: string;
    available_weeks: string[];
    campaign_coverage_alerts: {
      key_name: string;
      coverage_pct: number;
      covered_lw_campaigns: number;
      total_lw_campaigns: number;
      avg_weekly_campaigns_6w: number;
      recommended_action: 'send' | 'skip';
      skip_reason: string | null;
      drafted_message: string | null;
      do_not_setup_campaigns: number;
      local_time_campaigns: number;
      smart_send_time_campaigns: number;
      seed_list_campaigns: number;
      short_lead_time_campaigns: number;
      missed_setup_campaigns: number;
      notes: string;
      no_setup_object_campaigns: number;
      campaign_details: { campaign_id: string; name: string; covered: boolean; flags: string[] }[];
      action_taken: '' | 'SKIPPED' | 'SENT_MESSAGE';
    }[];
  };
}

export async function getOrderRateLiftAlerts(weekStart?: string) {
  const response = await client.GET(
    '/optimizer/get_order_rate_lift_alerts/' as any, // oxlint-disable-line typescript/no-explicit-any
    { params: { query: weekStart ? { week_start: weekStart } : {} } },
  );
  assertDefined(response.data);
  return response.data as {
    week_of: string | null;
    available_weeks: string[];
    order_rate_lift_alerts: {
      key_name: string;
      most_recent_lift: number | null;
      six_week_avg: number | null;
      incremental_roi_30d: number | null;
      incremental_roi_prior_30d: number | null;
      alert_types: ('negative_lift' | 'significant_drop' | 'roi_drop')[];
      root_causes: { label: string; evidence: string; is_noise: boolean }[];
      recommended_action: 'no action' | 'investigate';
      notes: string;
      action_taken: '' | 'NO_ACTION' | 'MONITORING' | 'MODIFIED_STORE_SETUP';
      deliverability_setup: {
        testing_split: string;
        sender_variant_b: string;
        sender_variant_c: string;
        deliverability_block_variant_b: string;
        deliverability_block_variant_c: string;
      } | null;
    }[];
  };
}

export async function recordOrderRateLiftAction(
  keyName: string,
  actionTaken: 'NO_ACTION' | 'MONITORING' | 'MODIFIED_STORE_SETUP',
  notes: string,
) {
  // oxlint-disable-next-line typescript/no-explicit-any, typescript/no-unsafe-assignment, typescript/no-unsafe-call
  const response: { data: { ok: boolean } | undefined } = await (client.POST as any)(
    '/optimizer/record_order_rate_lift_action/',
    { body: { key_name: keyName, action_taken: actionTaken, notes } },
  );
  assertDefined(response.data);
  return response.data;
}

export async function recordCampaignCoverageAction(
  keyName: string,
  actionTaken: 'SKIPPED' | 'SENT_MESSAGE',
  notes: string,
) {
  // oxlint-disable-next-line typescript/no-explicit-any, typescript/no-unsafe-assignment, typescript/no-unsafe-call
  const response: { data: { ok: boolean } | undefined } = await (client.POST as any)(
    '/optimizer/record_campaign_coverage_action/',
    { body: { key_name: keyName, action_taken: actionTaken, notes } },
  );
  assertDefined(response.data);
  return response.data;
}

export async function saveOrderRateLiftNotes(keyName: string, notes: string) {
  // oxlint-disable-next-line typescript/no-explicit-any, typescript/no-unsafe-assignment, typescript/no-unsafe-call
  const response: { data: { ok: boolean } | undefined } = await (client.POST as any)(
    '/optimizer/record_order_rate_lift_notes/',
    { body: { key_name: keyName, notes } },
  );
  assertDefined(response.data);
  return response.data;
}

export async function saveCampaignCoverageNotes(keyName: string, notes: string) {
  // oxlint-disable-next-line typescript/no-explicit-any, typescript/no-unsafe-assignment, typescript/no-unsafe-call
  const response: { data: { ok: boolean } | undefined } = await (client.POST as any)(
    '/optimizer/record_campaign_coverage_notes/',
    { body: { key_name: keyName, notes } },
  );
  assertDefined(response.data);
  return response.data;
}

export async function getAllPendingFlowMessages(
  page = 1,
  pageSize = 200,
  tab: 'pending' | 'skipped' = 'pending',
  storeKeyName?: string,
) {
  const response = await client.GET(
    '/optimizer/get_all_pending_flow_messages/' as any, // oxlint-disable-line typescript/no-explicit-any
    {
      params: {
        query: {
          page,
          page_size: pageSize,
          tab,
          ...(storeKeyName ? { store_key_name: storeKeyName } : {}),
        },
      },
    },
  );
  assertDefined(response.data);
  return response.data as {
    count: number;
    store_ids: number[];
    pending_flow_messages: {
      klaviyo_account_organization_name: string;
      flow_id: number;
      flow_name: string;
      flow_message_id: number;
      flow_message_is_top_15_revenue: boolean;
      flow_message_has_750_plus_sends: boolean;
      flow_message_name: string;
      flow_message_sends_last_30_days: number | undefined;
      flow_message_opens_last_30_days: number | undefined;
      flow_message_clicks_last_30_days: number | undefined;
      flow_message_purchases_last_30_days: number | undefined;
      flow_message_setup_skipped_reason: SetupSkippedReasonEnum;
      flow_action_status: string;
      klaviyo_flow_id: string;
      klaviyo_flow_message_id: string;
      sp_optimizer_store_id: number;
      sp_optimizer_flow_message_experiment_id: number;
      sp_optimizer_flow_message_experiment_enabled: boolean;
      sp_optimizer_flow_message_experiment_message_type: string;
      sp_optimizer_flow_message_experiment_klaviyo_in_sync: boolean;
      sp_optimizer_flow_message_experiment_maverick_profile_property_variant_type_key: string;
      sp_optimizer_flow_message_experiment_admin_url: string;
      sp_optimizer_flow_message_experiment_email_preview_link: string;
      sp_optimizer_flow_message_experiment_enabled_by_username: string;
      store_owner_organization_slug: string;
      store_id: number;
      store_key_name: string;
      delivered: number;
      opened: number;
      clicked: number;
      total_purchases: number;
    }[];
  };
}

export async function getAllNeedUniversalBlockExperiments(
  page = 1,
  storeKeyName?: string,
  flowMessageId?: string,
): Promise<{
  count: number;
  next: string | null;
  previous: string | null;
  results: {
    flowMessageId: string;
    flowId: number;
    flowMessageDatabaseId: number;
    klaviyoAccountOrganizationName: string;
    spOptimizerFlowMessageExperimentId: number;
    spOptimizerFlowMessageExperimentStatus: string;
    spOptimizerFlowMessageExperimentMessageType: string;
    hasMaverickOptimizerFlowUniversalBlock: boolean;
    storeId: number;
    storeKeyName: string;
    enabled: boolean;
    disabledReason: string;
  }[];
}> {
  const params: Record<string, string> = { page: String(page) };
  if (storeKeyName) {
    params.store_key_name = storeKeyName;
  }
  if (flowMessageId) {
    params.flow_message_id = flowMessageId;
  }
  const response = await client.GET(
    '/optimizer/flow_message_experiment/need-universal-block/' as any, // oxlint-disable-line typescript/no-explicit-any
    {
      params: {
        query: params,
      },
    },
  );
  assertDefined(response.data);
  return response.data as {
    count: number;
    next: string | null;
    previous: string | null;
    results: {
      flowMessageId: string;
      flowId: number;
      flowMessageDatabaseId: number;
      klaviyoAccountOrganizationName: string;
      spOptimizerFlowMessageExperimentId: number;
      spOptimizerFlowMessageExperimentStatus: string;
      spOptimizerFlowMessageExperimentMessageType: string;
      hasMaverickOptimizerFlowUniversalBlock: boolean;
      storeId: number;
      storeKeyName: string;
      enabled: boolean;
      disabledReason: string;
    }[];
  };
}

export async function postRefreshExperimentUniversalBlock(experimentId: number) {
  const response = await client.POST(
    `/optimizer/flow_message_experiment/${experimentId}/refresh-universal-block/` as any, // oxlint-disable-line typescript/no-explicit-any
    {},
  );
  assertDefined(response.data);
  return response.data as {
    hasMaverickOptimizerFlowUniversalBlock: boolean;
    hasGroup1MaverickOptimizerFlowUniversalBlock: boolean;
    spOptimizerFlowMessageExperimentId: number;
  };
}

export async function getAllFlowMessagesNeedDeliverabilityBlockUpgrade(
  page = 1,
  storeKeyName?: string,
  flowMessageId?: string,
): Promise<{
  count: number;
  next: string | null;
  previous: string | null;
  results: {
    flowMessageId: string;
    flowId: number;
    flowMessageDatabaseId: number;
    klaviyoAccountOrganizationName: string;
    spOptimizerFlowMessageExperimentId: number;
    spOptimizerFlowMessageExperimentStatus: string;
    spOptimizerFlowMessageExperimentMessageType: string;
    editorType: string;
    storeKeyName: string;
    needsGroup1BlockRemoved: boolean;
    needsRenderVarBlockAdded: boolean;
  }[];
}> {
  const params: Record<string, string> = { page: String(page) };
  if (storeKeyName) {
    params.store_key_name = storeKeyName;
  }
  if (flowMessageId) {
    params.flow_message_id = flowMessageId;
  }
  const response = await client.GET(
    '/optimizer/flow_message_experiment/need-deliverability-block-upgrade/' as any, // oxlint-disable-line typescript/no-explicit-any
    {
      params: {
        query: params,
      },
    },
  );
  assertDefined(response.data);
  return response.data as {
    count: number;
    next: string | null;
    previous: string | null;
    results: {
      flowMessageId: string;
      flowId: number;
      flowMessageDatabaseId: number;
      klaviyoAccountOrganizationName: string;
      spOptimizerFlowMessageExperimentId: number;
      spOptimizerFlowMessageExperimentStatus: string;
      spOptimizerFlowMessageExperimentMessageType: string;
      editorType: string;
      storeKeyName: string;
      needsGroup1BlockRemoved: boolean;
      needsRenderVarBlockAdded: boolean;
    }[];
  };
}

export async function postRefreshExperimentKlaviyoInSync(experimentId: number): Promise<{
  klaviyoInSync: boolean;
  isTransactional: boolean;
  hasBlockStructure: boolean;
  blockReferenceCount: number;
  spOptimizerFlowMessageExperimentId: number;
}> {
  const response = await client.POST(
    `/optimizer/flow_message_experiment/${experimentId}/refresh-klaviyo-in-sync/` as any, // oxlint-disable-line typescript/no-explicit-any
    {},
  );
  assertDefined(response.data);
  // oxlint-disable-next-line typescript/no-unsafe-return
  return response.data;
}

export async function getAllMisconfiguredExperiments() {
  const response = await client.GET(
    '/optimizer/get_all_misconfigured_experiments/' as any, // oxlint-disable-line typescript/no-explicit-any
    {
      params: {
        query: {},
      },
    },
  );
  assertDefined(response.data);
  return response.data as {
    misconfigured_experiments: {
      flow_message_id: string;
      flow_id: number;
      flow_message_database_id: number;
      flow_message_setup_skipped_reason: string;
      klaviyo_account_organization_name: string;
      sp_optimizer_flow_message_experiment_id: number;
      sp_optimizer_flow_message_experiment_status: string;
      sp_optimizer_flow_message_experiment_message_type: string;
      sp_optimizer_flow_message_experiment_enabled: boolean;
      store_id: number;
      store_key_name: string;
    }[];
  };
}

export async function postSetExperimentFlowMessageSetupSkippedReason(
  experimentId: number,
  setupSkippedReason: '' | 'UNEDITABLE_EMAIL',
): Promise<{
  spOptimizerFlowMessageExperimentId: number;
  spOptimizerFlowMessageExperimentEnabled: boolean;
  flowMessageSetupSkippedReason: string;
}> {
  const response = await client.POST(
    `/optimizer/flow_message_experiment/${experimentId}/set-flow-message-setup-skipped-reason/` as any, // oxlint-disable-line typescript/no-explicit-any
    {
      body: { setup_skipped_reason: setupSkippedReason } as any, // oxlint-disable-line typescript/no-explicit-any
    },
  );
  assertDefined(response.data);
  // oxlint-disable-next-line typescript/no-unsafe-return
  return response.data;
}

export async function getSPOptimizerStoreDeliverabilityBlockSetups() {
  const response = await client.GET('/optimizer/store_deliverability_block_setup/');
  assertDefined(response.data);
  return response.data;
}

/** The single current (latest-revision) deliverability block setup for one store, or null if none. */
export async function getSPOptimizerStoreDeliverabilityBlockSetup(spOptimizerStoreId: number) {
  const response = await client.GET('/optimizer/store_deliverability_block_setup/', {
    params: { query: { spOptimizerStore: spOptimizerStoreId } },
  });
  assertDefined(response.data);
  return response.data[0] ?? null;
}

export async function postCreateSPOptimizerStoreDeliverabilityBlockSetup(
  spOptimizerStoreId: number,
  senderVariantB: string,
  deliverabilityBlockVariantB: DeliverabilityBlockKeyEnum | '',
  senderVariantC: string,
  deliverabilityBlockVariantC: DeliverabilityBlockKeyEnum | '',
  testingSplit: TestingSplitEnum,
  additionalNotes: string,
  requiresGeneratedSummary: boolean,
) {
  const response = await client.POST('/optimizer/store_deliverability_block_setup/', {
    body: {
      spOptimizerStore: spOptimizerStoreId,
      senderVariantB: senderVariantB,
      deliverabilityBlockVariantB: deliverabilityBlockVariantB,
      senderVariantC: senderVariantC,
      deliverabilityBlockVariantC: deliverabilityBlockVariantC,
      testingSplit: testingSplit,
      additionalNotes: additionalNotes,
      requiresGeneratedSummary: requiresGeneratedSummary,
    },
  });
  assertDefined(response.data);
  return response.data;
}

export async function patchSPOptimizerStoreDeliverabilityBlockSetup(
  storeDeliverabilityBlockSetupId: number,
  senderVariantB: string,
  deliverabilityBlockVariantB: DeliverabilityBlockKeyEnum | '',
  senderVariantC: string,
  deliverabilityBlockVariantC: DeliverabilityBlockKeyEnum | '',
  testingSplit: TestingSplitEnum,
  additionalNotes: string,
  requiresGeneratedSummary: boolean,
) {
  const response = await client.PATCH('/optimizer/store_deliverability_block_setup/{id}/', {
    params: {
      path: {
        id: storeDeliverabilityBlockSetupId,
      },
    },
    body: {
      senderVariantB: senderVariantB,
      deliverabilityBlockVariantB: deliverabilityBlockVariantB,
      senderVariantC: senderVariantC,
      deliverabilityBlockVariantC: deliverabilityBlockVariantC,
      testingSplit: testingSplit,
      additionalNotes: additionalNotes,
      requiresGeneratedSummary: requiresGeneratedSummary,
    },
  });
  assertDefined(response.data);
  return response.data;
}

export async function patchFlowMessageSetupSkippedReason(
  flowMessageId: number,
  setupSkippedReason: SetupSkippedReasonEnum | undefined | '',
) {
  const response = await client.PATCH('/klaviyo/flow_messages/{id}/set_setup_skipped_reason/', {
    params: {
      path: {
        id: flowMessageId,
      },
    },
    body: {
      setupSkippedReason: setupSkippedReason ?? '',
    },
  });
  assertDefined(response.data);
  return response.data;
}

export async function getFlowMessageExperimentAllocationConversionStats(
  organization: string,
  spOptimizerFlowMessageExperimentId: number,
) {
  const response = await client.GET(
    '/optimizer/flow_message_experiment/{id}/flow_message_experiment_allocation_conversion_stats/',
    {
      params: {
        path: {
          id: spOptimizerFlowMessageExperimentId,
        },
        query: {
          organization: organization,
          combineBrandOriginals: true,
        },
      },
    },
  );
  assertDefined(response.data);
  return response.data;
}

export async function getFlowMessageExperimentTargetMetrics(
  organization: string,
  spOptimizerFlowMessageExperimentId: number,
) {
  const response = await client.GET('/optimizer/flow_message_experiment/{id}/all_target_metrics/', {
    params: {
      path: {
        id: spOptimizerFlowMessageExperimentId,
      },
      query: { organization: organization },
    },
  });
  assertDefined(response.data);
  return response.data;
}

export async function getSPOptimizerFlowMessageExperimentVariantsForExperiment(
  organization: string,
  spOptimizerFlowMessageExperimentId: number,
  onlyActive?: boolean,
) {
  const response = await client.GET('/optimizer/flow_message_experiment/{id}/get_variants_on_experiment/', {
    params: {
      path: { id: spOptimizerFlowMessageExperimentId },
      query: {
        organization: organization,
        onlyActive: onlyActive ?? false,
      },
    },
  });
  assertDefined(response.data);
  return response.data;
}

export async function getSPOptimizerFlowMessageExperimentKlaviyoTemplates(
  organization: string,
  spOptimizerFlowMessageExperimentId: number,
) {
  const response = await client.GET('/optimizer/flow_message_experiment/{id}/klaviyo_templates/', {
    params: {
      path: { id: spOptimizerFlowMessageExperimentId },
      query: { organization: organization },
    },
  });
  assertDefined(response.data);
  return response.data;
}

export async function getOptimizerCampaignDeliverabilityExperimentMetrics(
  organization: string,
  spOptimizerStoreID: number,
  days: number,
  startDate?: string,
  endDate?: string,
  includeLikelihood = false,
) {
  const response = await client.GET('/optimizer/store/{id}/campaign_deliverability_experiment_metrics/', {
    params: {
      path: {
        id: mapMaverickDemoOptimizerStoreId[spOptimizerStoreID] ?? spOptimizerStoreID,
      },
      query: {
        organization: mapMaverickDemoOrganization[organization] ?? organization,
        days: days,
        ...(startDate && { startDate }),
        ...(endDate && { endDate }),
        ...(includeLikelihood && { includeLikelihood: true }),
      },
    },
  });
  assertDefined(response.data);
  return response.data;
}

export async function getSetupDecisions(filters?: {
  status?: SPOptimizerSetupDecisionStatusEnum;
  spOptimizerStore?: number;
}) {
  const response = await client.GET('/optimizer/setup_decisions/', {
    params: {
      query: {
        ...(filters?.status && { status: filters.status }),
        ...(filters?.spOptimizerStore !== undefined && { spOptimizerStore: filters.spOptimizerStore }),
      },
    },
  });
  assertDefined(response.data);
  return response.data;
}

// The {id} detail-action path param isn't expressed in the generated types (drf-spectacular emits
// `path: never` for custom @action routes), so bypass the typed client — openapi-fetch still
// substitutes {id} from params.path at runtime.
export async function applySetupDecision(id: number): Promise<void> {
  // oxlint-disable-next-line typescript/no-explicit-any, typescript/no-unsafe-call
  await (client.POST as any)('/optimizer/setup_decisions/{id}/apply/', { params: { path: { id } } });
}

export async function dismissSetupDecision(id: number): Promise<void> {
  // oxlint-disable-next-line typescript/no-explicit-any, typescript/no-unsafe-call
  await (client.POST as any)('/optimizer/setup_decisions/{id}/dismiss/', { params: { path: { id } } });
}

export async function getCampaignPrimaryConversionMetricOptions(organization: string, spOptimizerStoreId: number) {
  const response = await client.GET('/optimizer/store/{id}/campaign_primary_conversion_metric_options/', {
    params: {
      path: { id: spOptimizerStoreId },
      query: { organization },
    },
  });
  assertDefined(response.data);
  return response.data;
}

export async function setCampaignPrimaryConversionMetric(
  organization: string,
  spOptimizerStoreId: number,
  metricId: number,
  metricType: 'metric' | 'custom_metric',
) {
  const response = await client.POST('/optimizer/store/{id}/set_campaign_primary_conversion_metric/', {
    params: {
      path: { id: spOptimizerStoreId },
      query: { organization },
    },
    body: { id: metricId, type: metricType },
  });
  assertDefined(response.data);
  return response.data;
}

export async function postAddPretesterInbox(): Promise<{ authorization_url: string }> {
  const response = await client.POST(
    '/optimizer/add_email_pretester_inbox/' as any, // oxlint-disable-line typescript/no-explicit-any
    {
      body: {},
    },
  );
  assertDefined(response.data);
  const responseData = response.data as { authorization_url: string };
  if (!responseData.authorization_url) {
    throw new Error('Failed to get authorization URL');
  }
  return responseData;
}

export async function getPretesterInboxes() {
  const response = await client.GET(
    '/optimizer/pretester_inboxes/' as any, // oxlint-disable-line typescript/no-explicit-any
    {
      params: {
        query: {},
      },
    },
  );
  assertDefined(response.data);
  return response.data as {
    pretester_inboxes: { id: number; email: string; first_name: string; last_name: string; active: boolean }[];
  };
}

export async function patchPretesterInboxName(email: string, firstName: string) {
  const response = await client.POST(
    '/optimizer/update_pretester_inbox_name/' as any, // oxlint-disable-line typescript/no-explicit-any
    {
      body: {
        first_name: firstName,
        email: email,
      },
    },
  );
  assertDefined(response.data);
  return response.data as { success: boolean };
}

export async function getSPOptimizerCampaignDeliverabilityBlockSetups(
  setupStatuses: string[],
  page = 1,
  pageSize = 50,
  storeID?: number,
  search?: string,
) {
  const response = await client.GET('/optimizer/campaign_deliverability_block_setup/', {
    params: {
      query: {
        setupStatus_In: [setupStatuses.join(',')],
        page: page,
        page_size: pageSize,
        ...(storeID && storeID !== 0 ? { sp_optimizer_store: storeID } : {}),
        ...(search?.trim() ? { search: search.trim() } : {}),
      },
    },
  });
  assertDefined(response.data);
  return response.data;
}

export async function getMissedCampaignDeliverabilityBlocks(
  page = 1,
  pageSize = 50,
  storeID?: number,
  search?: string,
) {
  const response = await client.GET('/optimizer/missed_campaign_deliverability_block/', {
    params: {
      query: {
        page: page,
        page_size: pageSize,
        ...(storeID && storeID !== 0 ? { sp_optimizer_store: storeID } : {}),
        ...(search?.trim() ? { search: search.trim() } : {}),
      },
    },
  });
  assertDefined(response.data);
  return response.data;
}

export async function patchSPOptimizerCampaignDeliverabilityBlockSetup(
  spOptimizerCampaignDeliverabilityBlockSetupId: number,
  setupStatus: SetupStatusEnum,
  notes: string,
) {
  const response = await client.PATCH('/optimizer/campaign_deliverability_block_setup/{id}/', {
    params: {
      path: {
        id: spOptimizerCampaignDeliverabilityBlockSetupId,
      },
    },
    body: {
      setupStatus: setupStatus,
      notes: notes,
    },
  });
  assertDefined(response.data);
  return response.data;
}

export async function getCampaignSetupAutomationPauseStatus() {
  const response = await client.GET('/optimizer/campaign_deliverability_block_setup/automation_pause_status/');
  assertDefined(response.data);
  return response.data;
}

export async function setCampaignSetupAutomationPauseStatus(paused: boolean, userAccount: UserAccountEnum) {
  const response = await client.POST('/optimizer/campaign_deliverability_block_setup/set_automation_pause_status/', {
    body: { paused, userAccount },
  });
  assertDefined(response.data);
  return response.data;
}

export async function getSlackChannelMappings() {
  const response = await client.GET('/slack-agent/channels/');
  assertDefined(response.data);
  return response.data;
}

export async function getSlackAnnouncements(page = 1) {
  const response = await client.GET('/slack-agent/announcements/', {
    params: { query: { page } },
  });
  assertDefined(response.data);
  return response.data;
}

export async function getSlackAnnouncement(id: number) {
  const response = await client.GET('/slack-agent/announcements/{id}/', {
    params: { path: { id } },
  });
  assertDefined(response.data);
  return response.data;
}

export async function postSlackAnnouncement(
  messageText: string,
  channelMappingIds: number[],
  excludedChannelMappingIds: number[],
) {
  const response = await client.POST('/slack-agent/announcements/', {
    body: {
      messageText,
      channelMappingIds,
      excludedChannelMappingIds,
    },
  });
  assertDefined(response.data);
  return response.data;
}

export async function getDataQualityReport() {
  const response = await client.GET('/mapi/data-quality-report/');
  assertDefined(response.data);
  return response.data;
}

export async function postDataQualityAssignSubscriptionStore(subscriptionId: number, storeId: number) {
  const response = await client.POST('/mapi/data-quality-report/assign-subscription-store/', {
    body: { subscriptionId, storeId },
  });
  assertDefined(response.data);
  return response.data;
}

export async function postDataQualityAssignDealStore(dealId: number, storeId: number) {
  const response = await client.POST('/mapi/data-quality-report/assign-deal-store/', {
    body: { dealId, storeId },
  });
  assertDefined(response.data);
  return response.data;
}

export async function postDataQualityAssignProductLabel(productId: number, maverickProduct: MaverickProductEnum) {
  const response = await client.POST('/mapi/data-quality-report/assign-product-label/', {
    body: { productId, maverickProduct },
  });
  assertDefined(response.data);
  return response.data;
}

export async function postDataQualityLinkDealSubscription(dealId: number, subscriptionId: number) {
  const response = await client.POST('/mapi/data-quality-report/link-deal-subscription/', {
    body: { dealId, subscriptionId },
  });
  assertDefined(response.data);
  return response.data;
}
